// ============================================================================
// Bid save status — one subscribable source for the autosave indicator.
// ----------------------------------------------------------------------------
// The estimator debounces and SERIALISES its writes in `bidStore` (a per-bid
// queue: one request in flight, later edits coalesced into the next save). This
// module exposes what that queue is doing so the UI can render a live
// "Saving… / Saved / Unsaved / Couldn't save" indicator.
//
// It follows the `financialDisplay` pattern: a `useSyncExternalStore` source
// with a STABLE snapshot reference (a fresh object per render would spin
// useSyncExternalStore forever), updated only when the status actually changes.
// ============================================================================

import { useSyncExternalStore } from 'react';

export type SaveStatus =
  | 'idle' // nothing typed yet, nothing to save
  | 'pending' // edits made, waiting out the debounce before the write fires
  | 'saving' // a request is in flight
  | 'saved' // the last write succeeded
  | 'error'; // the last write failed (the store also toasts + resyncs)

export interface SaveStatusSnapshot {
  status: SaveStatus;
  /** epoch ms of the last successful save, or null if none this session. */
  lastSavedAt: number | null;
}

/**
 * How long the transient "Saved" confirmation lingers before the indicator falls back to its
 * resting "Autosaves every Ns" hint — long enough to register, short enough that the user is
 * reminded saving is automatic rather than left staring at a stale "Saved".
 */
export const SAVED_LINGER_MS = 2000;

let snapshot: SaveStatusSnapshot = { status: 'idle', lastSavedAt: null };

/** Pending "Saved → idle" fallback, if one is armed. */
let revertTimer: ReturnType<typeof setTimeout> | null = null;

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Move to a new status. A new snapshot object is published (so subscribers see
 * a changed reference), but ONLY when something actually changed — an idle→idle
 * or a repeated status with no new save must not notify, or the indicator
 * re-renders in a loop.
 */
export function setSaveStatus(status: SaveStatus): void {
  const lastSavedAt = status === 'saved' ? Date.now() : snapshot.lastSavedAt;
  // A repeated 'saved' is meaningful (a second successful save bumps the time);
  // any other repeat with an unchanged timestamp is a no-op.
  if (status === snapshot.status && lastSavedAt === snapshot.lastSavedAt) return;
  // Any real status change cancels a pending "Saved → idle" fallback: a fresh edit ('pending'),
  // a new write ('saving'), or a failure ('error') must not be clobbered back to idle by a
  // stale timer.
  if (revertTimer) {
    clearTimeout(revertTimer);
    revertTimer = null;
  }
  snapshot = { status, lastSavedAt };
  emit();
  // "Saved" is a confirmation, not a resting state. After a beat, fall back to the "Autosaves
  // every Ns" hint so the user is reminded saving is automatic. Guarded so a status that changed
  // in the meantime (another save, an edit) is left untouched.
  if (status === 'saved') {
    revertTimer = setTimeout(() => {
      revertTimer = null;
      if (snapshot.status !== 'saved') return;
      snapshot = { status: 'idle', lastSavedAt: snapshot.lastSavedAt };
      emit();
    }, SAVED_LINGER_MS);
  }
}

export function getSaveStatusSnapshot(): SaveStatusSnapshot {
  return snapshot;
}

/** Test seam: reset back to a clean idle state between cases. */
export function resetSaveStatus(): void {
  if (revertTimer) {
    clearTimeout(revertTimer);
    revertTimer = null;
  }
  snapshot = { status: 'idle', lastSavedAt: null };
  emit();
}

/** Subscribe a component to the autosave status. */
export function useSaveStatus(): SaveStatusSnapshot {
  return useSyncExternalStore(subscribe, getSaveStatusSnapshot, getSaveStatusSnapshot);
}
